/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { assert } from '@fluidframework/common-utils';
import BTree from 'sorted-btree';
import LRU from 'lru-cache';
import { fail, compareFiniteNumbers } from './Common';
/**
 * A cache of `TValue`s corresponding to `Revision`s.
 *
 * A value is kept in cache if it meets any of the following criteria:
 *
 * - The revision is \>= `retentionWindowStart`
 *
 * - The value has been used recently, meaning getClosestEntry or cacheValue was called with its revision. Note that
 * being returned when a large revision was passed to getClosestEntry does not count.
 *
 * - The value is `retained` meaning it was provided to to constructor in retainedEntries or passed to
 * `cacheRetainedValue`
 */
export class RevisionValueCache {
    constructor(
    /**
     * Maximum capacity for evictable cache entries (those neither marked as retained nor within the retention window).
     */
    evictableSize, 
    /**
     * The first revision within the retention window. All entries with revisions \>= retentionWindowStart will be retained.
     * Must be \>= 0.
     */
    retentionWindowStart, 
    /**
     * The oldest revision that must be retained in memory.
     */
    retainedRevision) {
        this.retentionWindowStart = retentionWindowStart;
        /**
         * A cache of entries for revisions.
         * This is sorted to allow efficient access to the nearest preceding entry (see getClosestEntry).
         * Contains all cached values, regardless of why they are cached (retained, LRU or window).
         */
        this.sortedEntries = new BTree(undefined, compareFiniteNumbers);
        assert(retentionWindowStart >= 0, 0x62c /* retentionWindowStart must be initialized >= 0 */);
        this.evictableRevisions = new LRU({
            max: evictableSize,
            noDisposeOnSet: true,
            dispose: (revision) => {
                if (revision >= this.retentionWindowStart) {
                    fail('Entries in retention window should never be evicted.');
                }
                if (this.retainedRevision === revision) {
                    fail('Retained entries should not be evicted');
                }
                this.sortedEntries.delete(revision);
            },
        });
        if (retainedRevision !== undefined) {
            this.cacheRetainedValue(retainedRevision[0], retainedRevision[1]);
        }
    }
    /**
     * @returns if the supplied revision is within the retention window.
     */
    isWithinRetentionWindow(revision) {
        return revision >= this.retentionWindowStart;
    }
    /**
     * Sets the new retention window.
     * @param newRetentionWindowStart - defines the trailing edge (inclusive) of the new retention window.
     */
    updateRetentionWindow(newRetentionWindowStart) {
        if (newRetentionWindowStart < this.retentionWindowStart) {
            fail('retention window boundary must not move backwards');
        }
        const prevRetentionWindowStart = this.retentionWindowStart;
        this.retentionWindowStart = newRetentionWindowStart;
        const oldWindowEntries = [];
        this.sortedEntries.forRange(prevRetentionWindowStart, this.retentionWindowStart, false, (windowRevision, windowEntry) => {
            if (this.retainedRevision !== windowRevision) {
                // Adding to the LRU can cause eviction which in turn mutates the b-tree we are enumerating. Thus, store list of
                // old window entries separately.
                oldWindowEntries.push([windowRevision, windowEntry]);
            }
        });
        oldWindowEntries.forEach(([revision, value]) => {
            this.evictableRevisions.set(revision, value);
        });
    }
    /**
     * @returns a [cachedRevision, value] where cachedRevision \<= requestedRevision, or undefined if no such revision
     * is cached.
     */
    getClosestEntry(requestedRevision) {
        var _a;
        const fromLRU = this.evictableRevisions.get(requestedRevision);
        if (fromLRU !== undefined) {
            return [requestedRevision, fromLRU];
        }
        return (_a = this.sortedEntries.getPairOrNextLower(requestedRevision)) !== null && _a !== void 0 ? _a : undefined;
    }
    /**
     * Caches the supplied value and guarantees it will never be evicted.
     * This will make the previously retained value evictable.
     */
    cacheRetainedValue(revision, value) {
        if (this.retainedRevision !== undefined) {
            this.sortedEntries.delete(this.retainedRevision);
        }
        this.retainedRevision = revision;
        this.sortedEntries.set(revision, value);
    }
    /**
     * Caches the supplied value.
     * The cached value is subject to eviction unless it is within the retention window or was previously added
     * via `cacheRetainedValue`.
     * Note that if a non-retained entry starts out within the retention window and passes outside of it due to a call to
     * updateRetentionWindow it is then subject to eviction.
     */
    cacheValue(revision, value) {
        if (this.retainedRevision === revision) {
            return;
        }
        this.sortedEntries.set(revision, value);
        if (revision < this.retentionWindowStart) {
            this.evictableRevisions.set(revision, value);
        }
    }
}
//# sourceMappingURL=RevisionValueCache.js.map